import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from "../../Services"
import { SystemData } from '../../Assets/TestData/systems';
import { Farm_Data, User_Data } from '../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/getCalls';
import { useAppSelector } from '../../Store';
import { useTranslation } from 'react-i18next';
import { colors, css, fonts, frames, glyphs, useMediaQuery } from '../../Styles';
import Setting from '../../Components/Settings/setting';
import { InputField, InputFieldList, InputFieldList_PropHandle, SelectField, TimeField, PageHeader, Button, ButtonList} from '../../Components';
import { FieldTypes, FieldStyles, ListStyle_Types, ButtonTypes, ListData_ValueTypes, TableStyles_Types } from '../../Definitions';

import TimezonePicker from '../../Components/BaseComponents/timezonePicker';

import { TestData as Milestones } from '../../Assets/TestData/milestones';
import { ReactiveTable } from '../../Components/Tables/reactiveTableRenderer';
import { AICO_IO_Data } from '../../Assets/TestData/controllers';
import { useEditableColumnConstructor } from '../../Components/Tables/_Tables/useEditableColumnConstructor';
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { InputField_Handles } from '../../Components/BaseComponents/input';


export default function GlobalSettings() {
  const {t} = useTranslation()
  const [feedDayStart, setFeedDayStart] = useState<Date>(new Date(new Date().setHours(0, 0, 0, 0)));
  const { userToken, getUserData, userData, updateUserData, getFarmData, farmData: farmDataArray, updateFarmData  } = useUser() 
  const activefarm = useAppSelector<string|undefined>(({activeData}) => activeData?.farm)
  const userInputRef=useRef<InputFieldList_PropHandle>(null)
  const farmInputRef=useRef<InputFieldList_PropHandle>(null)
  const hideBtnText = useMediaQuery(`(max-width: 450px)`);
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const mobileXs = useMediaQuery(`(max-width: 570px)`);

  useEffect(() => {
    if (userToken) {
      getUserData(userToken);
      if (activefarm) {
        getFarmData(activefarm);
      }
    }
  }, []); 

  // #region | User Settings
  const loadUserData = useCallback(() => {
    if (userData !== undefined) {
      userInputRef.current?.refs.current[0].setContext(userData.firstname)
      userInputRef.current?.refs.current[1].setContext(userData.lastname)
      userInputRef.current?.refs.current[2].setContext(userData.phone)
      userInputRef.current?.refs.current[3].setContext(userData.email)
      userInputRef.current?.refs.current[4].setContext(userData.address)
      userInputRef.current?.refs.current[5].setContext(userData.country)
      userInputRef.current?.refs.current[6].setContext(userData.username)
    }
  }, [userData])

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleUserDataUpdate = () => {
    var NewUserData = {
      firstname: userInputRef.current?.refs.current[0].context,
      lastname: userInputRef.current?.refs.current[1].context,
      phone: userInputRef.current?.refs.current[2].context,
      email: userInputRef.current?.refs.current[3].context,
      address: userInputRef.current?.refs.current[4].context,
      country: userInputRef.current?.refs.current[5].context
    } as User_Data
    updateUserData(NewUserData.firstname, NewUserData.lastname, NewUserData.phone, NewUserData.email, NewUserData.address, NewUserData.country)
  }
  // #endregion

  // #region | Farm Settings
  const loadFarmData = useCallback(() => {
    if (farmDataArray !== undefined) {
      const farmData = farmDataArray[0];
      farmInputRef.current?.refs.current[0].setContext(farmData.name);
      farmInputRef.current?.refs.current[1].setContext(farmData.city);
      farmInputRef.current?.refs.current[2].setContext(farmData.street);
      farmInputRef.current?.refs.current[3].setContext(farmData.phone);
      farmInputRef.current?.refs.current[4].setContext(farmData.vat);
      farmInputRef.current?.refs.current[5].setContext(farmData.email);
    }
  }, [farmDataArray]);
  
  useEffect(() => {
    loadFarmData();
  }, [loadFarmData]);

  const handleFarmDataUpdate = () => {
    var NewFarmData = {
      name: farmInputRef.current?.refs.current[0].context,
      city: farmInputRef.current?.refs.current[1].context,
      street: farmInputRef.current?.refs.current[2].context,
      phone: farmInputRef.current?.refs.current[3].context,
      vat: farmInputRef.current?.refs.current[4].context,
      email: farmInputRef.current?.refs.current[5].context
    } as Farm_Data
    console.log(farmInputRef.current)
    updateFarmData( NewFarmData.name, NewFarmData.city, NewFarmData.street, NewFarmData.phone, NewFarmData.vat, NewFarmData.email, activefarm)
  }
  // #endregion

  // #region | Units
  const [unit, setUnit] = useState<number>(0)

  const unitsOptions = [    
    { label: t("units.metric"), id: 0 },
    { label: t("units.imperial"), id: 1 },
  ]
  // #endregion

  // # region | Alert when boardname already excists
  const inputRef=useRef<InputField_Handles>(null)
  const handleCheckBoardName = () => {
    if (inputRef.current) {
      const name: string = (inputRef.current?.context as string).toUpperCase();
      const nameExists = boardData.some(board => board.name.toUpperCase() === name);
  
      if (nameExists) {
        alert('Board name already exists!');
      } else {
        console.log(name); // TODO: Save board to db
      }
    }
  };
  //# endregion

  // #region | Board Types
  const [boardType, setBoardType] = useState<number>(0)

  const boardTypeOptions = [    
    { label: "Board Type 1", id: 0 },
    { label: "Board Type 2", id: 1 },
    { label: "Board Type 3", id: 2 },
    { label: "Board Type 4", id: 3 }
  ]
  // #endregion

  // #region | Systems
  const [system, setSystem] = useState<number>(1)

  const systemOptions = SystemData.map(e => ({
    label: e.systemName,
    id: e.systemId
  }));
  // #endregion

  // Find label in select field options corresponding to their respective ID
  const findLabelById = (id: number, options: { label: string, id: number }[]) => {
    const option = options.find(option => option.id === id);
    return option ? option.label : "";
  };

  // #region | Boards Table
  const boardData = useMemo(() => (AICO_IO_Data.map((board) => {
    const system = SystemData.find(sys => sys.systemId === board.systemId);
            const systemName = system && (system.systemId === 2) 
            ? system.systemName 
            : "No System Assigned";
    return {
      id: board.id,
      name: board.name,
      type: board.type,
      system: systemName
    }
  })),[])

  const accessorData = useMemo(() => ([
    {headerValue: "Id", accessor: "id", type: ListData_ValueTypes.Number, width: "120px"},
    {headerValue: "Name", accessor: "name", type: ListData_ValueTypes.String},
    {headerValue: "Type", accessor: "type", type: ListData_ValueTypes.Number},
    {headerValue: "System", accessor: "system", type: ListData_ValueTypes.String},
  ]),[])
  
  const columns = useEditableColumnConstructor({accessorData, tableStyle: TableStyles_Types.newSecondaryTable, canEdit: false });
  
  const table = useReactTable({
    columns,
    data: boardData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel()
  })
  
  const reactiveTable = useMemo(() => {if (boardData.length!==0) {return ReactiveTable({table, tableType: TableStyles_Types.newSecondaryTable, emptyText: "Empty text", renderFooter: false})}}, [table, boardData.length])
  // #endregion

  return (
    <frames.Page_inner>
    <frames._page_inner_header>
      <div style={{display: "flex", flexDirection: "row", alignItems: "start", justifyContent: "space-between"}}>
        <PageHeader topHeader={t("globalMenu.globalSettings")} subHeader={t("overview")}/>
        <ButtonList 
          contents={[t("buttons.saveBtn")+" "+t("changes"), t("buttons.discard")+" "+t("changes")]} 
          onClick={(i => {
            if (i === 0) {handleUserDataUpdate(); handleFarmDataUpdate()}
            else if (i === 1) {loadUserData(); loadFarmData()}
          })}
          buttonType={ButtonTypes.primaryButton}
          overrides={{
            baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none"} : {backgroundColor: colors.greyDarkSecondary})}
          }}
          listOverrides={{
            listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
          }}
          responsiveOverrides={{
            isResponsive: !hideBtns ? false : true,
            popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" },
            openContent: hideBtnText ? "" : "Options"
          }}          
        />
      </div>
    </frames._page_inner_header>
    <frames._page_inner_content_scroll>
      <div style={{display: "flex", gap: "20px", alignItems: "center", justifyContent: "center", flexDirection: "column"}}>
        
        {/* User Settings */}
        <Setting startEnhancer={glyphs.User({size: "25px"})} header={t("settingsPages.globalSettings.userSettings")} content={
          <InputFieldList ref={userInputRef}
            placeholder={[t("user.firstName")+"...", t("user.lastName")+"...", t("user.phone")+"...", t("user.email")+"...", t("user.country")+"...", t("user.address")+"...", t("user.userName")+"..." ]} 
            fieldStyle={FieldStyles.greyTertiary} 
            fieldType={FieldTypes.primaryInputField} 
            isRequired={Array.from({ length: 8 }, () => false)} 
            listStyle={mobileXs ? ListStyle_Types.centeredGroup : ListStyle_Types.group} 
            textStart={Array.from({ length: 8 }, () => true)}
            disabled={Array.from({ length: 8 }, (_, index) => index === 6)}
          />
        }/>

        {/* Farm Settings */}
        <Setting startEnhancer={glyphs.Animal({size: "25px"})} header={t("settingsPages.globalSettings.farmSettings")} content={
          <InputFieldList ref={farmInputRef} 
            placeholder={[t("farm.farmName")+"...", t("farm.city")+"...", t("farm.street")+"...", t("user.phone")+"...", t("farm.vat")+"...", t("user.email")+"..."]} 
            fieldStyle={FieldStyles.greyTertiary} 
            fieldType={FieldTypes.primaryInputField} 
            isRequired={Array.from({ length: 6 }, () => true)} 
            listStyle={mobileXs ? ListStyle_Types.centeredGroup : ListStyle_Types.group} 
            textStart={Array.from({ length: 6 }, () => true)} 
            disabled={Array.from({ length: 8 }, (_, index) => index === 0)}
          />
        }/>

        {/* User Management */}
        <Setting startEnhancer={glyphs.User({size: "25px"})} header={t("settingsPages.globalSettings.userManagement")} content={
          <>
            <div style={{display: "flex", flexWrap: "wrap", gap: "5px", justifyContent: mobileXs ? "center" : "start"}}>
              <InputField placeholder={t("user.user")+" "+t("user.email")+"..."} fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.primaryInputField} textStart={true} />
              <div style={{display: "flex", flexDirection: "row", gap: "5px"}}>
                <ButtonList
                  contents={[t("buttons.add"), t("buttons.remove")]}
                  buttonType={ButtonTypes.textButton}
                  overrides={{
                    baseButton: {backgroundColor: colors.greyLightPrimary, height: "30px", color: colors.greyDarkPrimary, ...fonts.fontSize4, padding: "0 10px"}
                  }}
                />
              </div>
            </div>
            <div style={{height: "2px", backgroundColor: colors.greyLightPrimary, width: "100%", margin: "15px 0"}}/>
          </>
        }/>

        {/* Time Zone */}
        <Setting startEnhancer={glyphs.Time({size: "25px"})} header={t("settingsPages.globalSettings.timeZone")} content={
          <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
            <TimezonePicker fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.primaryInputField} textStart={true}/>
          </div>
        }/>

        {/* Units */}
        <Setting startEnhancer={glyphs.Kg({size: "25px"})} header={t("settingsPages.globalSettings.units")} content={
          <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
            <SelectField 
              defaultValue={findLabelById(unit, unitsOptions)}
              options={unitsOptions}
              onChange={(e) => setUnit(unit)}
              overrides={{
                fieldStyle: FieldStyles.greyTertiary,
                fieldType: FieldTypes.primaryInputField,
                textStart: true
              }}
            />
          </div>
        }/>

        {/* Feed Day Start */}
        <Setting startEnhancer={glyphs.TimeToday({size: "25px"})} header={t("settingsPages.globalSettings.feedDayStart")} content={
          <div style={{display: "flex", justifyContent: mobileXs ? "center" : "start"}}>
            <TimeField textStart={true} fieldStyle={FieldStyles.greyTertiary} value={feedDayStart} onChange={(date: Date) => {
              setFeedDayStart(new Date(date.setHours(0, 0, 0, 0)));
            }}/>
          </div>
        }/>

        {/* Milestones */}
        <Setting startEnhancer={glyphs.Setting({size: "25px"})} header={t("feed.feedPlan.milestones")} content={
          <>    
            <div style={{display: "flex", flexWrap: "wrap", gap: "5px", justifyContent: mobileXs ? "center" : "start"}}>
              <InputField placeholder={t("settingsPages.globalSettings.newMilestone")+"..."} fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.primaryInputField} textStart={true} />
              <Button
                  content={t("buttons.add")}
                  buttonType={ButtonTypes.textButton}
                  overrides={{
                    baseButton: {backgroundColor: colors.greyLightPrimary, height: "30px", color: colors.greyDarkPrimary, ...fonts.fontSize4, padding: "0 10px"}
                  }}
                />
            </div>
            <div style={{height: "2px", backgroundColor: colors.greyLightPrimary, width: "100%", margin: "15px 0"}}/>
            <div style={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: mobileXs ? "center" : "start"}}>
              {Milestones.map((milestone) => (
                <div style={{backgroundColor: colors.greyPrimary, ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary, display: "flex", flexDirection: "row", gap: "5px", alignItems: "center" }} key={milestone.milestoneId}>
                  {milestone.milestoneName} 
                  <div 
                    style={{cursor: "pointer"}}
                    // onClick={() => deleteMilestone()} TODO
                   >
                  <glyphs.Close size="10px"/>
                  </div>
                </div>
              ))}
            </div>
          </>
        }/>

        {/* I/O Boards */}
        <Setting startEnhancer={glyphs.User({size: "25px"})} overrides={{content: {padding: "0px", height: "100%", maxHeight: "300px", overflow: "hidden", borderBottomRightRadius: "5px"}}} header={"I/O Boards"} content={
          <>
            <div style={{padding: mobileXs ? "10px 10px 0px 10px" : "10px 20px 0px 20px"}}>
              <div style={{display: "flex", flexWrap: "wrap", gap: "5px", maxWidth: mobileXs ? "280px" : "auto", margin: mobileXs ? "0 auto" : "none"}}>
                <InputField ref={inputRef}
                  placeholder={"Board Name..."} 
                  fieldStyle={FieldStyles.greyTertiary} 
                  fieldType={FieldTypes.shortInputField} 
                  textStart={true}
                />
                <SelectField 
                  defaultValue={findLabelById(boardType, boardTypeOptions)}
                  options={boardTypeOptions}
                  onChange={(e) => setBoardType(boardType)}
                  overrides={{
                    fieldStyle: FieldStyles.greyTertiary,
                    fieldType: FieldTypes.shortInputField,
                    textStart: true
                  }}
                />
                <SelectField 
                  overrides={{
                    fieldStyle: FieldStyles.greyTertiary,
                    fieldType: FieldTypes.shortInputField,
                    textStart: true
                  }}
                  defaultValue={findLabelById(system, systemOptions)}
                  options={systemOptions}
                  onChange={(e) => setSystem(system)}
                />
                <div style={{display: "flex", flexDirection: "row", gap: "5px"}}>
                  <ButtonList
                    contents={[t("buttons.add")]}
                    buttonType={ButtonTypes.textButton}
                    onClick={handleCheckBoardName}
                    overrides={{
                      baseButton: {backgroundColor: colors.greyLightPrimary, height: "30px", color: colors.greyDarkPrimary, ...fonts.fontSize4, padding: "0 10px"}
                    }}
                  />
                </div>
              </div>
              <div style={{height: "2px", backgroundColor: colors.greyLightPrimary, width: "100%", margin: "15px 0px 0px 0px"}}/>
            </div>
            {reactiveTable}
          </>
        }/>

      </div>
    </frames._page_inner_content_scroll>
    </frames.Page_inner>
  );
}